import {
  BUSINESS_KIND_LABELS,
  BUSINESS_KIND_LIST_LABELS,
  type BusinessKind,
  LEAD_SEGMENT_LIST_LABELS,
  type LeadSegment,
  LEAD_COLUMNS,
  LEAD_SORT_FIELDS,
  type LeadColumn,
  type LeadFilter,
  type LeadSelection,
  type LeadSortField,
  type MissingField,
} from '@hearth/shared';
import { pluralize } from '../../lib/format';

/**
 * Pure helpers for the lead table: URL ↔ filter state, saved column preferences and selection maths.
 * Keeping these out of the component makes them testable and keeps the page readable.
 */

/** The list each kind of business appears in (every profile stays at /leads/:id). */
export const KIND_LIST_PATHS: Record<BusinessKind, string> = { pizzeria: '/leads', dealer: '/dealers', rep: '/reps' };

/** Pizzerias are split by hand into Leads, Prospects (engaged), Clients (already customers) and Test Kitchens. */
export const SEGMENT_LIST_PATHS: Record<LeadSegment, string> = {
  lead: '/leads',
  prospect: '/prospects',
  client: '/clients',
  test_kitchen: '/test-kitchens',
};

/** The list a business appears in: pizzerias by segment, dealers and reps by kind. */
export function listOf(business: { kind: BusinessKind; segment: LeadSegment }): { path: string; label: string } {
  return business.kind === 'pizzeria'
    ? { path: SEGMENT_LIST_PATHS[business.segment], label: LEAD_SEGMENT_LIST_LABELS[business.segment] }
    : { path: KIND_LIST_PATHS[business.kind], label: BUSINESS_KIND_LIST_LABELS[business.kind] };
}

/** What one business of a kind is called on screen: pizzerias are our "leads"; the others go by their kind. */
export function kindNoun(kind: BusinessKind): string {
  return kind === 'pizzeria' ? 'lead' : BUSINESS_KIND_LABELS[kind].toLowerCase();
}

/** "3 leads", "3 dealers", "1 rep". */
export function kindCount(kind: BusinessKind, count: number): string {
  return pluralize(count, kindNoun(kind), BUSINESS_KIND_LIST_LABELS[kind].toLowerCase());
}

/** "Pizzerias", "Dealers", "Reps": what a file's businesses are (the list titles say "Leads" for pizzerias). */
export function kindPluralLabel(kind: BusinessKind): string {
  return `${BUSINESS_KIND_LABELS[kind]}s`;
}

/** Filters that only mean something for pizzerias: fit score, tier, pizza focus and ownership. */
const PIZZERIA_ONLY_FILTERS = ['tierKeys', 'minScore', 'maxScore', 'pizzaFocus', 'ownershipTypes'] as const satisfies readonly (keyof LeadFilter)[];

/**
 * The filter as it applies on a kind's list. Saved views are shared by every list, so a view's pizzeria-only filters
 * are ignored on Dealers and Reps instead of emptying them.
 */
export function filterForKind(filter: LeadFilter, kind: BusinessKind): LeadFilter {
  if (kind === 'pizzeria') return filter;
  const next = { ...filter };
  for (const key of PIZZERIA_ONLY_FILTERS) delete next[key];
  return next;
}

/**
 * What a list sends (list, export, bulk selection, bulk email): the page's kind — and for pizzerias its segment — is
 * added here, never in the URL or a view.
 */
export function requestFilter(filter: LeadFilter, kind: BusinessKind, segment: LeadSegment): LeadFilter {
  return { ...filterForKind(filter, kind), kinds: [kind], ...(kind === 'pizzeria' ? { segments: [segment] } : {}) };
}

export type TableState = {
  filter: LeadFilter;
  sort: LeadSortField;
  dir: 'asc' | 'desc';
  page: number;
  pageSize: number;
  viewId: string | null;
};

export const DEFAULT_TABLE_STATE: TableState = { filter: {}, sort: 'score', dir: 'desc', page: 1, pageSize: 50, viewId: null };

/** What a list shows until the user changes it: the URL only carries what differs from these. */
export type TableDefaults = { sort: LeadSortField; dir: 'asc' | 'desc'; columns: LeadColumn[] };

const PIZZERIA_COLUMNS: LeadColumn[] = ['business', 'city', 'score', 'tier', 'stage', 'relationship', 'contact', 'lastContactedAt', 'nextFollowUpAt', 'estimatedValue', 'owner'];

/** Pizzerias rank by fit score; dealers and reps have no score, so they list alphabetically without score/tier columns. */
export function tableDefaults(kind: BusinessKind): TableDefaults {
  return kind === 'pizzeria'
    ? { sort: DEFAULT_TABLE_STATE.sort, dir: DEFAULT_TABLE_STATE.dir, columns: PIZZERIA_COLUMNS }
    : { sort: 'name', dir: 'asc', columns: PIZZERIA_COLUMNS.filter((column) => column !== 'score' && column !== 'tier') };
}

export const PAGE_SIZES = [25, 50, 100, 200];

const ARRAY_FIELDS = [
  'owner',
  'stageKeys',
  'relationshipKeys',
  'tierKeys',
  'sourceIds',
  'tagIds',
  'ownershipTypes',
  'pizzaFocus',
  'opportunityStages',
  'missing',
  'nextActions',
] as const;

const BOOLEAN_FIELDS = ['hasOpenOpportunity', 'neverContacted', 'doNotContact', 'hasPendingDuplicate', 'deleted'] as const;

const NUMBER_FIELDS = [
  'minScore',
  'maxScore',
  'lastContactOlderThanDays',
  'createdWithinDays',
] as const;

/** Read the table state from the URL so views are shareable and back/forward work. */
export function stateFromParams(params: URLSearchParams, defaults: TableDefaults = tableDefaults('pizzeria')): TableState {
  const filter: Record<string, unknown> = {};
  const text = (key: string) => {
    const value = params.get(key);
    return value === null || value === '' ? undefined : value;
  };
  const q = text('q');
  if (q) filter.q = q;
  const city = text('city');
  if (city) filter.city = city;
  const region = text('region');
  if (region) filter.region = region;
  const country = text('country');
  if (country) filter.country = country;
  const territoryId = text('territoryId');
  if (territoryId) filter.territoryId = territoryId;
  const followUp = text('followUp');
  if (followUp) filter.followUp = followUp as LeadFilter['followUp'];
  const importJobId = text('importJobId');
  if (importJobId) filter.importJobId = importJobId;
  const importBatchId = text('importBatchId');
  if (importBatchId) filter.importBatchId = importBatchId;

  for (const key of ARRAY_FIELDS) {
    const value = text(key);
    if (value) filter[key] = value.split(',').filter(Boolean);
  }
  for (const key of BOOLEAN_FIELDS) {
    const value = text(key);
    if (value === 'true') filter[key] = true;
    if (value === 'false') filter[key] = false;
  }
  for (const key of NUMBER_FIELDS) {
    const value = text(key);
    if (value !== undefined && Number.isFinite(Number(value))) filter[key] = Number(value);
  }

  // Only known sort fields reach the API — a hand-edited URL must not produce a 400.
  const sort = text('sort');
  const dir = text('dir');
  const page = text('page');
  const pageSizeRaw = text('pageSize');
  return {
    filter: filter as LeadFilter,
    sort: sort && (LEAD_SORT_FIELDS as readonly string[]).includes(sort) ? (sort as LeadSortField) : defaults.sort,
    dir: dir === 'asc' || dir === 'desc' ? dir : defaults.dir,
    page: page ? Math.max(1, Number(page)) : 1,
    pageSize: pageSizeRaw && PAGE_SIZES.includes(Number(pageSizeRaw)) ? Number(pageSizeRaw) : DEFAULT_TABLE_STATE.pageSize,
    viewId: text('view') ?? null,
  };
}

/** Serialize the state back into query parameters (empty values are omitted). */
export function paramsFromState(state: TableState, columns: LeadColumn[], defaults: TableDefaults = tableDefaults('pizzeria')): URLSearchParams {
  const params = new URLSearchParams();
  const filter = state.filter as Record<string, unknown>;
  for (const [key, value] of Object.entries(filter)) {
    if (value === undefined || value === null || value === '' || value === false) continue;
    if (Array.isArray(value)) {
      if (value.length > 0) params.set(key, value.join(','));
      continue;
    }
    params.set(key, String(value));
  }
  if (state.sort !== defaults.sort) params.set('sort', state.sort);
  if (state.dir !== defaults.dir) params.set('dir', state.dir);
  if (state.page !== 1) params.set('page', String(state.page));
  if (state.pageSize !== DEFAULT_TABLE_STATE.pageSize) params.set('pageSize', String(state.pageSize));
  if (state.viewId) params.set('view', state.viewId);
  if (columns.join(',') !== defaults.columns.join(',')) params.set('columns', columns.join(','));
  return params;
}

export function columnsFromParams(params: URLSearchParams): LeadColumn[] | null {
  const raw = params.get('columns');
  if (!raw) return null;
  const columns = raw.split(',').filter((c): c is LeadColumn => (LEAD_COLUMNS as readonly string[]).includes(c));
  return columns.length > 0 ? columns : null;
}

/** Each list remembers its own columns; pizzerias keep the original key so existing preferences survive. */
function columnStorageKey(kind: BusinessKind): string {
  return kind === 'pizzeria' ? 'hearth.leadColumns' : `hearth.leadColumns.${kind}`;
}

export function loadColumnPreference(kind: BusinessKind, storage: Pick<Storage, 'getItem'> | undefined = globalThis.localStorage): LeadColumn[] | null {
  try {
    const raw = storage?.getItem(columnStorageKey(kind));
    if (!raw) return null;
    const columns = JSON.parse(raw) as unknown;
    if (!Array.isArray(columns)) return null;
    const valid = columns.filter((c): c is LeadColumn => typeof c === 'string' && (LEAD_COLUMNS as readonly string[]).includes(c));
    return valid.length > 0 ? valid : null;
  } catch {
    return null;
  }
}

export function saveColumnPreference(kind: BusinessKind, columns: LeadColumn[], storage: Pick<Storage, 'setItem'> | undefined = globalThis.localStorage): void {
  try {
    storage?.setItem(columnStorageKey(kind), JSON.stringify(columns));
  } catch {
    // A full or unavailable localStorage must never break the table.
  }
}

/** Create an empty state (mostly for tests). */
export function emptyState(overrides: Partial<TableState> = {}): TableState {
  return { ...DEFAULT_TABLE_STATE, filter: {}, ...overrides };
}

/** A selection is either explicit ids or "everything matching the filter, minus exclusions". */
export function selectionFor(ids: string[], filter: LeadFilter, mode: 'ids' | 'filter'): LeadSelection {
  return mode === 'filter' ? { mode: 'filter', filter, excludeIds: [] } : { mode: 'ids', ids };
}

export function selectionCount(selection: LeadSelection, ids: string[], total: number): number {
  return selection.mode === 'ids' ? selection.ids.length : Math.max(0, total - selection.excludeIds.length);
}

export function toggleId(ids: string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((value) => value !== id) : [...ids, id];
}

export function allOnPageSelected(pageIds: string[], selected: string[]): boolean {
  return pageIds.length > 0 && pageIds.every((id) => selected.includes(id));
}

/** Human chips for the active filters, with the patch that removes each one. */
export type FilterChip = { key: string; label: string; clear: Partial<LeadFilter> };

export function filterChips(
  filter: LeadFilter,
  labels: {
    stage: (key: string) => string;
    relationship: (key: string) => string;
    tier: (key: string) => string;
    user: (id: string) => string;
    tag: (id: string) => string;
    source: (id: string) => string;
    /** File name of an import, for "From import …". */
    importJob: (id: string) => string;
    /** Number of files in a folder import, when known. */
    importBatchFiles: (id: string) => number | undefined;
  },
): FilterChip[] {
  const chips: FilterChip[] = [];
  if (filter.q) chips.push({ key: 'q', label: `“${filter.q}”`, clear: { q: undefined } });
  if (filter.importJobId) chips.push({ key: 'import', label: `From import “${labels.importJob(filter.importJobId)}”`, clear: { importJobId: undefined } });
  if (filter.importBatchId) {
    const files = labels.importBatchFiles(filter.importBatchId);
    const label = files === undefined ? 'From a folder import' : `From a folder import (${files} file${files === 1 ? '' : 's'})`;
    chips.push({ key: 'importBatch', label, clear: { importBatchId: undefined } });
  }
  if (filter.city) chips.push({ key: 'city', label: filter.city, clear: { city: undefined } });
  if (filter.region) chips.push({ key: 'region', label: filter.region, clear: { region: undefined } });
  for (const key of filter.stageKeys ?? []) {
    chips.push({ key: `stage:${key}`, label: labels.stage(key), clear: { stageKeys: (filter.stageKeys ?? []).filter((k) => k !== key) } });
  }
  for (const key of filter.relationshipKeys ?? []) {
    chips.push({
      key: `relationship:${key}`,
      label: labels.relationship(key),
      clear: { relationshipKeys: (filter.relationshipKeys ?? []).filter((k) => k !== key) },
    });
  }
  for (const key of filter.tierKeys ?? []) {
    chips.push({ key: `tier:${key}`, label: labels.tier(key), clear: { tierKeys: (filter.tierKeys ?? []).filter((k) => k !== key) } });
  }
  for (const id of filter.owner ?? []) {
    chips.push({ key: `owner:${id}`, label: id === 'me' ? 'Assigned to me' : id === 'unassigned' ? 'Unassigned' : labels.user(id), clear: { owner: (filter.owner ?? []).filter((v) => v !== id) } });
  }
  for (const id of filter.tagIds ?? []) {
    chips.push({ key: `tag:${id}`, label: labels.tag(id), clear: { tagIds: (filter.tagIds ?? []).filter((v) => v !== id) } });
  }
  for (const id of filter.sourceIds ?? []) {
    chips.push({ key: `source:${id}`, label: labels.source(id), clear: { sourceIds: (filter.sourceIds ?? []).filter((v) => v !== id) } });
  }
  for (const key of filter.ownershipTypes ?? []) {
    chips.push({ key: `ownership:${key}`, label: key, clear: { ownershipTypes: (filter.ownershipTypes ?? []).filter((v) => v !== key) } });
  }
  for (const key of filter.opportunityStages ?? []) {
    chips.push({
      key: `oppStage:${key}`,
      label: `Deal: ${key.replace(/_/g, ' ')}`,
      clear: { opportunityStages: (filter.opportunityStages ?? []).filter((v) => v !== key) },
    });
  }
  for (const key of filter.nextActions ?? []) {
    chips.push({ key: `nextAction:${key}`, label: key.replace(/_/g, ' '), clear: { nextActions: (filter.nextActions ?? []).filter((v) => v !== key) } });
  }
  for (const key of filter.missing ?? []) {
    chips.push({
      key: `missing:${key}`,
      label: `Missing ${MISSING_LABELS[key]}`,
      clear: { missing: (filter.missing ?? []).filter((v) => v !== key) },
    });
  }
  if (filter.minScore !== undefined || filter.maxScore !== undefined) {
    chips.push({
      key: 'score',
      label: `Score ${filter.minScore ?? 0}–${filter.maxScore ?? 100}`,
      clear: { minScore: undefined, maxScore: undefined },
    });
  }
  if (filter.lastContactOlderThanDays !== undefined) {
    chips.push({
      key: 'lastContact',
      label: `No contact in ${filter.lastContactOlderThanDays} days`,
      clear: { lastContactOlderThanDays: undefined },
    });
  }
  if (filter.createdWithinDays !== undefined) {
    chips.push({ key: 'created', label: `Added in the last ${filter.createdWithinDays} days`, clear: { createdWithinDays: undefined } });
  }
  if (filter.neverContacted) chips.push({ key: 'neverContacted', label: 'Never contacted', clear: { neverContacted: undefined } });
  if (filter.hasOpenOpportunity) chips.push({ key: 'openOpp', label: 'Has an open deal', clear: { hasOpenOpportunity: undefined } });
  if (filter.followUp) chips.push({ key: 'followUp', label: FOLLOW_UP_LABELS[filter.followUp], clear: { followUp: undefined } });
  if (filter.doNotContact) chips.push({ key: 'dnc', label: 'Do not contact', clear: { doNotContact: undefined } });
  if (filter.deleted) chips.push({ key: 'deleted', label: 'Deleted leads', clear: { deleted: undefined } });
  return chips;
}

export const MISSING_LABELS: Record<MissingField, string> = {
  email: 'email',
  phone: 'phone',
  contact: 'contact',
  decision_maker: 'decision maker',
  address: 'address',
  website: 'website',
};

const FOLLOW_UP_LABELS: Record<NonNullable<LeadFilter['followUp']>, string> = {
  overdue: 'Follow-up overdue',
  today: 'Follow-up due today',
  week: 'Follow-up this week',
  none: 'No follow-up scheduled',
};

/** Merge a chip's `clear` patch into a filter, dropping empty arrays. */
export function applyPatch(filter: LeadFilter, patch: Partial<LeadFilter>): LeadFilter {
  const next: Record<string, unknown> = { ...filter, ...patch };
  for (const [key, value] of Object.entries(next)) {
    if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) delete next[key];
  }
  return next as LeadFilter;
}

export function activeFilterCount(filter: LeadFilter): number {
  return Object.keys(filter).length;
}
